/**
 * This is to gather stats on the amount a user has read, for the purposes of
 * calculating their daily streak and for reporting of motivational stats.
 */

class ReadingTracker {
  startingPosition: number;
  startingTime: number;
  estimatedWPM = 180;

  previousInteractionTime: number;
  totalReadingTime = 0;

  /** This is needed since the change where some spans contain punctuation instead of words */
  wordsPerSpan: number;

  /**
   * Index 0 corresponds to `startingPosition`
   * true if we think the user has read this word, false or undefined if not
   */
  readPosition: boolean[] = [];

  constructor(startingPosition: number, now: number, wordsPerSpan: number) {
    this.startingPosition = startingPosition;
    this.startingTime = now ?? new Date().getTime();
    this.wordsPerSpan = wordsPerSpan;

    this.previousInteractionTime = this.startingTime;
  }

  /**
   * Report an interaction at a specific position on the page. This could be translating a word,
   * or turning a page (which would correspond to the last word on the page).
   *
   * @param position - the position of the word or phrase translated. If a phrase, it's the
   *                   position of the LAST word in the phrase.
   */
  reportInteraction(position: number, now?: number) {
    now = now ?? new Date().getTime();

    const maxTimePerInteraction = 1.5 * 60 * 1000; // 1.5 minutes

    let cappedTimeSinceLastInteraction = Math.min(
      maxTimePerInteraction,
      now - this.previousInteractionTime
    );

    const upperEstimateOfSpansRead =
      ((cappedTimeSinceLastInteraction / (60 * 1000)) *
        this.estimatedWPM *
        1.5) /
      this.wordsPerSpan;

    const wordsReadBefore =
      this.readPosition.filter((x) => x).length * this.wordsPerSpan;

    // Populate the readPosition array with true values leading up to the new position
    for (
      let spanNumber = 0;
      spanNumber < upperEstimateOfSpansRead;
      spanNumber++
    ) {
      if (position - this.startingPosition - spanNumber >= 0) {
        this.readPosition[position - this.startingPosition - spanNumber] = true;
      }
    }

    const wordsReadAfter =
      this.readPosition.filter((x) => x).length * this.wordsPerSpan;

    // Further cap reading time for this segment assuming the user can't read slower than 1/3
    // their current WPM estimate
    cappedTimeSinceLastInteraction = Math.min(
      (wordsReadAfter - wordsReadBefore) / (this.estimatedWPM / 60 / 1000 / 3),
      cappedTimeSinceLastInteraction
    );

    this.previousInteractionTime = now;
    this.totalReadingTime += cappedTimeSinceLastInteraction;
  }

  getStats(): {
    wordsRead: number;
    timeReading: number;
    WPM: number;
    fractionWordsRead: number;
  } {
    const wordsRead = Math.round(
      this.readPosition.filter((x) => x).length * this.wordsPerSpan
    );

    return {
      wordsRead,
      timeReading: this.totalReadingTime,
      WPM: (wordsRead * 60 * 1000) / this.totalReadingTime,
      fractionWordsRead:
        wordsRead / (this.readPosition.length * this.wordsPerSpan),
    };
  }
}

export default ReadingTracker;
